import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import Modal from '@/components/ui/Modal';
import Button from '@/components/ui/Button';
import DataTableWithQuery from '@/components/ui/DataTableWithQuery';
import LocationTargetSelector from '@/components/ads/LocationTargetSelector';
import { userSelectionColumns } from '@/components/users/userSelectionColumns';
import { useDisplayAdCampaignNow } from '@/hooks/useAdCampaigns';
import { useAppUsers } from '@/hooks/useUsers';
import { AdCampaignDisplayNowMode, AdCampaignLocationTarget } from '@/types/ads';

type DisplayNowModalProps = {
  isOpen: boolean;
  onClose: () => void;
  campaignId: string;
  campaignName: string;
};

const DisplayNowModal = ({ isOpen, onClose, campaignId, campaignName }: DisplayNowModalProps) => {
  const [displayNowMode, setDisplayNowMode] = useState<AdCampaignDisplayNowMode>('ALL');
  const [displayNowUsers, setDisplayNowUsers] = useState<string[]>([]);
  const [displayNowLocations, setDisplayNowLocations] = useState<AdCampaignLocationTarget[]>([]);
  const displayNowMutation = useDisplayAdCampaignNow();

  useEffect(() => {
    if (!isOpen) return;
    setDisplayNowMode('ALL');
    setDisplayNowUsers([]);
    setDisplayNowLocations([]);
  }, [isOpen]);

  const handleClose = () => {
    onClose();
  };

  const handleDisplayNow = async () => {
    if (!campaignId) return;
    if (displayNowMode === 'USERS') {
      const selectedIds = displayNowUsers
        .map((id) => Number(id))
        .filter((id) => Number.isFinite(id));
      if (selectedIds.length === 0) {
        toast.error('Selecciona al menos un usuario');
        return;
      }
      try {
        await displayNowMutation.mutateAsync({
          id: campaignId,
          data: { mode: 'USERS', userIds: selectedIds },
        });
        toast.success(`Solicitud enviada`);
        handleClose();
      } catch (error) {
        toast.error(error instanceof Error ? error.message : 'No se pudo enviar la solicitud');
      }
      return;
    }

    if (displayNowMode === 'LOCATION') {
      if (displayNowLocations.length === 0) {
        toast.error('Agrega al menos una ubicación');
        return;
      }
      try {
        await displayNowMutation.mutateAsync({
          id: campaignId,
          data: { mode: 'LOCATION', locationTargets: displayNowLocations },
        });
        toast.success(`Solicitud enviada`);
        handleClose();
      } catch (error) {
        toast.error(error instanceof Error ? error.message : 'No se pudo enviar la solicitud');
      }
      return;
    }

    try {
      await displayNowMutation.mutateAsync({
        id: campaignId,
        data: { mode: 'ALL' },
      });
      toast.success(`Solicitud enviada`);
      handleClose();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'No se pudo enviar la solicitud');
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Mostrar campaña ahora" size="xl">
      <div className="space-y-4">
        <div className="rounded-md border border-indigo-100 bg-indigo-50 px-4 py-3 text-sm text-indigo-900">
          <span className="font-semibold">Campaña:</span> <span>{campaignName}</span>
        </div>
        <div className="space-y-3">
          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium text-gray-700">Alcance</label>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
              {[
                { value: 'ALL', label: 'Mostrar a todos' },
                { value: 'USERS', label: 'Mostrar a usuarios específicos' },
                { value: 'LOCATION', label: 'Mostrar por ubicación' },
              ].map((option) => (
                <label
                  key={option.value}
                  className={`flex items-center gap-2 rounded-md border px-3 py-2 text-sm cursor-pointer ${
                    displayNowMode === option.value
                      ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                      : 'border-gray-200 text-gray-700'
                  }`}
                >
                  <input
                    type="radio"
                    name="displayNowMode"
                    value={option.value}
                    checked={displayNowMode === option.value}
                    onChange={() => {
                      setDisplayNowMode(option.value as AdCampaignDisplayNowMode);
                      setDisplayNowUsers([]);
                      setDisplayNowLocations([]);
                    }}
                  />
                  {option.label}
                </label>
              ))}
            </div>
          </div>

          {displayNowMode === 'ALL' && (
            <p className="text-sm text-gray-500">
              Se enviará la campaña a todos los usuarios activos con dispositivo vinculado.
            </p>
          )}

          {displayNowMode === 'USERS' && (
            <div className="space-y-2">
              <div className="text-sm text-gray-500">
                Seleccionados: <span className="font-semibold text-gray-900">{displayNowUsers.length}</span>
              </div>
              <DataTableWithQuery
                columns={userSelectionColumns}
                actions={[]}
                useQueryHook={useAppUsers}
                itemsPerPage={5}
                title=""
                searchPlaceholder="Buscar por nombre o usuario..."
                tableStyle="compact"
                enableRowSelection
                selectedRowIds={displayNowUsers}
                onSelectionChange={setDisplayNowUsers}
                getRowId={(user) => String(user.id)}
                className="shadow-none border border-gray-200"
              />
            </div>
          )}

          {displayNowMode === 'LOCATION' && (
            <LocationTargetSelector
              value={displayNowLocations}
              onChange={setDisplayNowLocations}
            />
          )}
        </div>
        <div className="flex items-center justify-end gap-3 pt-2">
          <Button variant="outline" onClick={handleClose}>
            Cancelar
          </Button>
          <Button
            onClick={handleDisplayNow}
            disabled={
              displayNowMutation.isPending
              || (displayNowMode === 'USERS' && displayNowUsers.length === 0)
              || (displayNowMode === 'LOCATION' && displayNowLocations.length === 0)
            }
          >
            Mostrar ahora
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default DisplayNowModal;
